import React, { useContext, useEffect, useMemo, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { Space } from 'antd';
import { isDesktop, isMobile } from 'react-device-detect';
import cloneDeep from 'lodash.clonedeep';
import { AvatarLetter } from 'components/avatar';
import Icon from 'components/icons';
import { INE_CONTACT_SUPPORT } from 'constants/settings';
import Switch from 'components/switch/Switch';
import Tooltip from 'components/tooltip';
import {
  AdviseContainer,
  AdviseInfo,
  BodyTitle,
  CenteredCell,
  Container,
  DeleteButton,
  EditContainer,
  EditFieldsLabel,
  EndCell,
  GetSkillDiveLink,
  HeaderAvailabilityCell,
  HeaderLabelWithInfo,
  HeaderRowContainer,
  HeaderTeamManagerRow,
  LabelEmail,
  LicenseBannerContainer,
  LicenseBannerText,
  NameEmailCell,
  RowContainer,
  SkillDiveEmail,
  SkillDivePlaceholder,
  SkillDiveSwitch,
  SkillDiveToggleRow,
  SkillDiveToggleWrapper,
  SwitchContainer,
  TeamManagerRow,
  TeamsCell,
  TitleContainer,
  UserTeamContainer,
  skillDiveColumns,
} from './styles';
import Button from 'components/button';
import { SessionContext } from 'contexts/session';
import { ExistingUserAccess, Invite } from '../AddByEmails/invitesReducer';
import ButtonIcon from 'components/buttonIcon';
import { AccountInvite } from '..';
import { UserTeam } from 'contexts/teams/types';
import { UserTeamsPayload } from 'components/usersTable/types';
import { LicenseViewer, licenseRoleLock } from 'components/usersTable/helper';
import LittleTeamsCard from 'components/cards/LittleTeamsCard';
import TeamSelectorMobile from 'components/TeamSelectMobile';
import useLicenseAvailability from 'hooks/useLicenseAvailability';
import useSkillDiveToggleState, {
  findAssignedSkillDive,
  skillDiveTooltip,
} from 'hooks/useSkillDiveToggleState';
import useCanGetSkillDive from 'hooks/useCanGetSkillDive';
import { FEATURE_FLAGS, useBooleanFlag } from 'hooks/useFeatureFlags';
import { CONTENT_TYPES } from 'constants/playlists';
import { ROLES } from 'constants/roles';
import { managerOfLabel } from 'constants/invites';
import {
  NO_TRAINING_LICENSES_BANNER,
  NO_TRAINING_LICENSES_TOOLTIP,
  seatsAvailable,
} from 'constants/licenses';
import {
  NOT_ON_PLAN_TOOLTIP_STYLE,
  NO_SKILL_DIVE_LICENSES_BANNER,
  NO_TRAINING_TOOLTIP_STYLE,
  SKILL_DIVE_CONTACT_HREF,
  SKILL_DIVE_LEARN_MORE,
  SKILL_DIVE_LEARN_MORE_URL,
  SKILL_DIVE_NOT_ON_PLAN,
  SKILL_DIVE_TOOLTIP_STYLE,
  skillDiveSwitchLabel,
} from 'constants/skill_dive';
import { licenseSeats, skillDiveSeats, takenLicenses, takenSkillDiveSeats } from 'utils/invites';
import { colors } from 'constants/styles/colors';

interface IAdjustUsersSettings {
  footer?: boolean;
  invites?: Invite[];
  activeUserInvites?: Invite[];
  onInvitesUpdate?: (invites: Invite[]) => void;
  onEdit: (open: boolean) => void;
  isEditing: boolean;
  onInviteRemove: (id: string) => void;
  hasTeam: boolean;
  onLicenseExcess?: (usedLicenses: number) => void;
  account: AccountInvite;
  inviteAccOwner?: boolean;
  /** Team modal: every row joins this one team, which can't be changed. */
  teamImplied?: boolean;
  /** Lets existing users' rows change their training and Skill Dive. */
  editsExistingUsers?: boolean;
}

const Advise: React.FC<{ title: string; body: string; buttonLabel: string }> = ({
  title,
  body,
  buttonLabel,
}) => (
  <AdviseContainer>
    <Icon stroke="#F28815" icon="AlertCircle" />
    <div className={'advice-inner-container'}>
      <AdviseInfo>
        <span>
          <b>{title}</b>
        </span>
        <p className="m-0">{body}</p>
      </AdviseInfo>
      <Button
        $secondary
        onClick={() => {
          window.location.href = `mailto:${INE_CONTACT_SUPPORT}`;
        }}
      >
        {buttonLabel}
      </Button>
    </div>
  </AdviseContainer>
);

// The Skill Dive flow's version of the banner above, as drawn for "no training licenses
// available".
const LicenseBanner: React.FC<{
  title: string;
  body: string;
  buttonLabel: string;
  href: string;
}> = ({ title, body, buttonLabel, href }) => (
  <LicenseBannerContainer>
    <Icon
      stroke={colors.performance.negative}
      icon="AlertCircle"
      style={{ width: '20px', height: '20px' }}
    />
    <LicenseBannerText>
      <b>{title}</b>
      <p>{body}</p>
    </LicenseBannerText>
    <Button
      $primary
      size="small"
      onClick={() => {
        window.location.href = href;
      }}
    >
      <FeatherIcon icon="mail" color={colors.palette.Business_DataVizLilacLighter} size={16} />
      {buttonLabel}
    </Button>
  </LicenseBannerContainer>
);

const isSkillDiveAddon = (addon: { name: string }) => addon.name === CONTENT_TYPES.SKILL_DIVE;

// The email flow keeps teams as `{ team: { id, name } }`, while the edit screen's team card and its
// manager handler read the flat `team_id` and `name`; fill those in. New objects, so editing them
// doesn't change the invite until it's saved.
const withFlatTeamFields = (userTeams: UserTeam[] = []): UserTeam[] =>
  userTeams.map((userTeam) => ({
    ...userTeam,
    team_id: userTeam.team_id || userTeam.team?.id,
    name: userTeam.name || userTeam.team?.name,
  }));

// Swaps the Skill Dive count for what's left after the seats toggled on in this batch, so the
// toggles go exhausted before submit. Other add-on rows are left as they are.
const useEffectiveSkillDivePool = (account: AccountInvite, skillDiveRemaining: number | null) =>
  useMemo(() => {
    if (skillDiveRemaining === null) return account.account_available_addons;
    return account.account_available_addons?.map((addon) =>
      isSkillDiveAddon(addon) ? { ...addon, count: skillDiveRemaining } : addon
    );
  }, [account.account_available_addons, skillDiveRemaining]);

// Tooltip when the plan doesn't allow removing a license.
const LICENSE_NOT_REMOVABLE_TOOLTIP =
  "Professional plan accounts don't have permission to remove licenses. Contact INE to upgrade.";

interface AccessStepViewer extends LicenseViewer {
  canUnassignLicense: boolean;
}

const useAccessStepViewer = (): AccessStepViewer => {
  const { user, userRoleIds, allows, isStaff } = useContext(SessionContext);
  return {
    uaaId: user?.uaa_id,
    email: user?.email,
    isStaff: Boolean(isStaff),
    isAccountOwner: Boolean(userRoleIds?.includes(ROLES.ACCOUNT_OWNER)),
    // Staff can remove licenses on any plan.
    canUnassignLicense: Boolean(isStaff || allows?.can_unnasign_license),
  };
};

// Why the signed-in user can't change this user's training or Skill Dive (users table rules).
const existingRoleLock = (
  row: Pick<Invite, 'email' | 'is_account_owner' | 'existing'>,
  viewer: AccessStepViewer
): string | null =>
  row.existing
    ? licenseRoleLock(
        {
          uaaId: row.existing.uaa_id,
          email: row.email,
          isAccountOwner: Boolean(row.is_account_owner),
          isTeamManager: Boolean(row.existing.is_team_manager),
        },
        viewer
      )
    : null;

// Why this user's training can't be changed: the plan can't remove it, or the role rules.
const existingLicenseLock = (
  row: Pick<Invite, 'email' | 'is_account_owner' | 'existing'>,
  viewer: AccessStepViewer
): string | null => {
  const { existing } = row;
  if (!existing) return null;
  if (existing.has_license && !viewer.canUnassignLicense) return LICENSE_NOT_REMOVABLE_TOOLTIP;
  return existingRoleLock(row, viewer);
};

// Locks the Skill Dive toggle when the role rules lock this user.
const withRoleLock = <T extends { disabled: boolean; tooltip: string }>(
  state: T,
  roleLock: string | null
): T => (roleLock ? { ...state, disabled: true, tooltip: roleLock } : state);

// A held seat follows the plan's removal rule. Once given up, it needs a free seat to be taken
// back if another row has taken one.
const skillDiveToggleSubject = (
  choice: { has_license?: boolean; has_skill_dive?: boolean },
  existing: ExistingUserAccess | undefined,
  canUnassignLicense: boolean,
  seatsGained: number
) =>
  existing && findAssignedSkillDive(existing) && (choice.has_skill_dive || !seatsGained)
    ? {
        user: { license: choice.has_license, license_addons: existing.license_addons },
        can_unnasign_license: canUnassignLicense,
      }
    : {
        invitee: { has_license: choice.has_license, has_skill_dive: choice.has_skill_dive },
        can_unnasign_license: true,
      };

// Its own component because the toggle-state hook can't be called inside the rows' .map().
const SkillDiveToggleCell: React.FC<{
  account: AccountInvite;
  invite: Invite;
  skillDiveRemaining: number | null;
  skillDiveGained: number;
  viewer: AccessStepViewer;
  onToggle: (checked: boolean, email: string) => void;
}> = ({ account, invite, skillDiveRemaining, skillDiveGained, viewer, onToggle }) => {
  const account_available_addons = useEffectiveSkillDivePool(account, skillDiveRemaining);
  const { disabled, tooltip } = withRoleLock(
    useSkillDiveToggleState({
      account: { account_available_addons },
      ...skillDiveToggleSubject(
        invite,
        invite.existing,
        viewer.canUnassignLicense,
        skillDiveGained
      ),
    }),
    existingRoleLock(invite, viewer)
  );
  return (
    <SkillDiveToggleWrapper $nonClickable={disabled} aria-disabled={disabled}>
      <Tooltip
        placement="top"
        destroyTooltipOnHide
        overlayInnerStyle={SKILL_DIVE_TOOLTIP_STYLE}
        title={skillDiveTooltip({ disabled, tooltip }, viewer.canUnassignLicense)}
      >
        <SkillDiveToggleRow>
          <SkillDiveSwitch
            aria-label={skillDiveSwitchLabel(invite.email)}
            checked={Boolean(invite.has_skill_dive)}
            $nonClickable={disabled}
            onChange={disabled ? undefined : (checked: boolean) => onToggle(checked, invite.email)}
          />
        </SkillDiveToggleRow>
      </Tooltip>
    </SkillDiveToggleWrapper>
  );
};

const EditInvite: React.FC<{
  footer: boolean;
  email: string;
  onInviteUpdate: ({ data, inviteEmail }) => void;
  onCancelInvite: () => void;
  onRemoveInvite: (email: string) => void;
  isManager: boolean;
  hasLicense: boolean;
  hasSkillDive: boolean;
  userTeams: UserTeam[];
  /** Training seats left after every row's saved choice; unlimited is Infinity. */
  licensesLeft: number;
  /** Training seats the rows take that they don't hold yet. */
  licensesGained: number;
  /** Why this user's training can't be changed, if it can't. */
  licenseLock: string | null;
  /** Why this user's Skill Dive can't be changed, if it can't. */
  skillDiveLock: string | null;
  isAccountOwner: boolean;
  account: AccountInvite;
  skillDiveRemaining: number | null;
  skillDiveGained: number;
  skillDiveFlow: boolean;
  /** An existing user's current access. */
  existing?: ExistingUserAccess;
  canChangeTeams: boolean;
}> = ({
  footer,
  email,
  onCancelInvite,
  onInviteUpdate,
  onRemoveInvite,
  hasLicense,
  hasSkillDive,
  isManager,
  userTeams,
  licensesLeft,
  licensesGained,
  licenseLock,
  skillDiveLock,
  isAccountOwner,
  account,
  skillDiveRemaining,
  skillDiveGained,
  skillDiveFlow,
  existing,
  canChangeTeams,
}) => {
  const { teams: accountTeams } = useContext(SessionContext);
  const { canUnassignLicense } = useAccessStepViewer();
  const [data, setData] = useState<{
    userTeams?: UserTeam[];
    has_license?: boolean;
    has_skill_dive?: boolean;
    is_manager?: boolean;
  }>({
    userTeams: skillDiveFlow ? withFlatTeamFields(userTeams) : userTeams,
    has_license: hasLicense,
    ...(skillDiveFlow && { has_skill_dive: hasSkillDive }),
    is_manager: isManager,
  });
  const handleChange = (value: any, type: string) => {
    setData((prevState) => {
      return {
        ...prevState,
        [type]: value,
      };
    });
  };

  // Turning training off turns Skill Dive off too, as handleSwitch does on desktop.
  const handleLicenseChange = (checked: boolean) => {
    handleChange(checked, 'has_license');
    if (skillDiveFlow && !checked) handleChange(false, 'has_skill_dive');
  };

  // The count passed in still holds this row's saved choice. Count the choice made here instead,
  // so a seat switched off here can be switched back on before saving.
  const seatsTaken = (choice: { has_license?: boolean; has_skill_dive?: boolean }) =>
    takenSkillDiveSeats([{ ...choice, existing }]);
  const localSkillDiveRemaining =
    skillDiveRemaining === null
      ? null
      : skillDiveRemaining +
        seatsTaken({ has_license: hasLicense, has_skill_dive: hasSkillDive }) -
        seatsTaken(data);
  const account_available_addons = useEffectiveSkillDivePool(account, localSkillDiveRemaining);
  const skillDiveToggleState = withRoleLock(
    useSkillDiveToggleState({
      account: { account_available_addons },
      ...skillDiveToggleSubject(data, existing, canUnassignLicense, skillDiveGained),
    }),
    skillDiveLock
  );

  const handleCancel = () => {
    onCancelInvite();
  };
  const handleUpdateInvite = () => {
    onInviteUpdate({ data, inviteEmail: email });
  };

  const handleSwitchManager = (teamsData: UserTeamsPayload[]) => {
    const userTeamIndex = data.userTeams.findIndex(
      ({ team_id }) => team_id === teamsData[0].team_id
    );
    // Copy the teams: rows can share team objects, and nothing should change until Save.
    const updatedUserTeams = data.userTeams.map((userTeam, index) =>
      index === userTeamIndex ? { ...userTeam, is_manager: teamsData[0].is_manager } : userTeam
    );
    setData((prevState) => {
      return {
        ...prevState,
        userTeams: updatedUserTeams,
      };
    });
  };

  const onSelectTeam = (teamIds: string[]) => {
    const newUserTeams = [];
    teamIds.forEach((ti) => {
      const userTeam = accountTeams.find((t) => t.id === ti);
      // Teams that stay selected keep their manager choice.
      const kept = skillDiveFlow ? data.userTeams?.find(({ team_id }) => team_id === ti) : null;
      newUserTeams.push({
        name: userTeam.name,
        team_id: ti,
        is_manager: Boolean(kept?.is_manager),
      });
    });
    setData((prevState) => {
      return {
        ...prevState,
        userTeams: newUserTeams,
      };
    });
  };

  return (
    <EditContainer>
      <div className={'invite-info'}>
        <div style={{ marginRight: '15px' }}>
          <AvatarLetter name={email} />
        </div>
        {skillDiveFlow ? (
          <SkillDiveEmail title={email} style={{ flex: 1 }}>
            {email}
          </SkillDiveEmail>
        ) : (
          <LabelEmail>{email}</LabelEmail>
        )}
        <ButtonIcon
          $terciary
          size={'small'}
          icon={'Trash'}
          style={{ margin: 'auto' }}
          onClick={() => onRemoveInvite(email)}
        />
      </div>
      <div className={'invite-switchs'}>
        <div>
          <EditFieldsLabel>{skillDiveFlow ? 'Training' : 'License'}</EditFieldsLabel>
          <Switch
            onChange={handleLicenseChange}
            disabled={
              (!(licensesLeft > 0) && !hasLicense && !(existing?.has_license && !licensesGained)) ||
              Boolean(licenseLock)
            }
            defaultChecked={hasLicense}
          />
        </div>
        {skillDiveFlow && (
          <div>
            <EditFieldsLabel>Skill Dive</EditFieldsLabel>
            <SkillDiveToggleWrapper
              $nonClickable={skillDiveToggleState.disabled}
              aria-disabled={skillDiveToggleState.disabled}
            >
              <Tooltip
                placement="top"
                destroyTooltipOnHide
                overlayInnerStyle={SKILL_DIVE_TOOLTIP_STYLE}
                title={skillDiveTooltip(skillDiveToggleState, canUnassignLicense)}
              >
                {/* Full size, to match the License switch beside it. */}
                <SkillDiveToggleRow>
                  <Switch
                    aria-label={skillDiveSwitchLabel(email)}
                    checked={Boolean(data.has_skill_dive)}
                    disabled={skillDiveToggleState.disabled}
                    onChange={
                      skillDiveToggleState.disabled
                        ? undefined
                        : (checked: boolean) => handleChange(checked, 'has_skill_dive')
                    }
                  />
                </SkillDiveToggleRow>
              </Tooltip>
            </SkillDiveToggleWrapper>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
        <LittleTeamsCard
          width="85"
          teams={data.userTeams}
          onSwitchManager={handleSwitchManager}
          // The account owner can't be made a team manager.
          disableManager={isAccountOwner}
          flat={false}
          managerControl={skillDiveFlow ? 'checkbox' : 'toggle'}
        />
        {canChangeTeams && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '15px',
              marginLeft: '10px',
            }}
          >
            <TeamSelectorMobile
              accountTeams={accountTeams}
              // The picker needs the flat `team_id`, which only the flag-on copy has.
              teamsSelected={skillDiveFlow ? data.userTeams : userTeams}
              onTeamChange={(teamIds) => onSelectTeam(teamIds)}
            />
          </div>
        )}
      </div>
      {footer && (
        <div className={'invite-edit-footer'}>
          <Button $secondary onClick={handleCancel}>
            Cancel
          </Button>
          <Button $primary onClick={handleUpdateInvite}>
            Save
          </Button>
        </div>
      )}
    </EditContainer>
  );
};

const AdjustUsersSettings: React.FC<IAdjustUsersSettings> = ({
  invites = [],
  activeUserInvites = [],
  onInvitesUpdate,
  onEdit,
  isEditing,
  onInviteRemove,
  hasTeam,
  account,
  inviteAccOwner,
  teamImplied,
  editsExistingUsers,
}) => {
  // Account owner invites don't take Skill Dive, so they keep today's step.
  const skillDiveFlow = useBooleanFlag(FEATURE_FLAGS.SKILL_DIVE_LICENSE_FLOW) && !inviteAccOwner;
  const viewer = useAccessStepViewer();
  const allInvites = [...invites, ...activeUserInvites];
  // Existing users count only when this step applies their changes.
  const seatTakers = editsExistingUsers ? allInvites : invites;
  const [usedLicenses, setUsedLicenses] = useState(takenLicenses(seatTakers));
  // Only `true` is unlimited; `false` is a pool with no seats, as the availability hook reads it.
  const licensesRemaining =
    typeof account.licenses_available === 'number' || account.licenses_available === false
      ? Number(account.licenses_available) - usedLicenses
      : '∞';
  // Numeric form for the switches. Below zero means more licenses assigned than the plan has.
  const licensesLeft = licensesRemaining === '∞' ? Number.POSITIVE_INFINITY : licensesRemaining;
  // A user who gave up their seat can take it back freely while no row takes a new one.
  const { gained: licensesGained } = licenseSeats(seatTakers);
  const { gained: skillDiveGained } = skillDiveSeats(seatTakers);
  const [inviteEdit, setInviteEdit] = useState<Invite>(null);

  const skillDivePool = account.account_available_addons?.find(isSkillDiveAddon);
  const skillDiveAvailability = useLicenseAvailability(skillDivePool);
  const canGetSkillDive = useCanGetSkillDive(account);

  const [usedSkillDive, setUsedSkillDive] = useState(takenSkillDiveSeats(seatTakers));
  const skillDiveRemaining =
    skillDiveAvailability.mode === 'count'
      ? (skillDiveAvailability.value ?? 0) - usedSkillDive
      : null;

  const handleRemoveInvite = (toRemove: string) => {
    onInviteRemove(toRemove);
    onEdit(false);
  };

  const handleSwitch = ({ checked, inviteEmail, type }) => {
    const inviteIndex = allInvites.findIndex(({ email }) => email === inviteEmail);
    const newInvites = [...allInvites];
    // Skill Dive needs a training license, so turning training off turns it off too.
    const dropsSkillDive =
      type === 'has_license' && !checked && Boolean(newInvites[inviteIndex].has_skill_dive);
    // Replace the row with a copy so the caller's rows aren't changed in place.
    newInvites[inviteIndex] = {
      ...newInvites[inviteIndex],
      [type]: checked,
      ...(dropsSkillDive && { has_skill_dive: false }),
    };
    if (dropsSkillDive) setUsedSkillDive((prev) => prev - 1);
    onInvitesUpdate(newInvites);
  };

  const handleSkillDiveSwitch = (checked: boolean, inviteEmail: string) => {
    handleSwitch({ checked, inviteEmail, type: 'has_skill_dive' });
    setUsedSkillDive((prev) => (checked ? prev + 1 : prev - 1));
  };

  const handleManagerSwitch = ({ checked, inviteEmail, teamId }) => {
    const inviteToUpdateIndex = allInvites.findIndex(({ email }) => email === inviteEmail);
    const inviteToUpdate = cloneDeep(allInvites.find(({ email }) => email === inviteEmail));
    const teamToUpdate = inviteToUpdate.userTeams.find((ut) => ut.team.id === teamId);
    teamToUpdate.is_manager = checked;
    const newInvites = cloneDeep(allInvites);
    newInvites[inviteToUpdateIndex] = inviteToUpdate;
    onInvitesUpdate(newInvites);
  };

  const handleRowClick = (selected: string) => {
    if (isMobile) {
      onEdit(true);
      setInviteEdit(allInvites.filter(({ email }) => email === selected)?.[0]);
    }
  };

  const handleUpdateEdit = ({ inviteEmail, data: { userTeams, has_license, has_skill_dive } }) => {
    const inviteIndex = allInvites.findIndex(({ email }) => email === inviteEmail);
    const newInvites = [...allInvites];
    // Invitees take Skill Dive; existing users only when this step applies their changes.
    const takesSkillDive = skillDiveFlow && (!newInvites[inviteIndex].id || editsExistingUsers);
    newInvites[inviteIndex] = {
      ...newInvites[inviteIndex],
      userTeams: userTeams,
      teams: userTeams.reduce((acc, userTeam) => {
        acc.push({ team_id: userTeam.team_id, is_manager: userTeam.is_manager });
        return acc;
      }, []),
      has_license: has_license || false,
      ...(takesSkillDive && { has_skill_dive: has_license ? has_skill_dive || false : false }),
    };
    onInvitesUpdate(newInvites);
    onEdit(false);
  };

  useEffect(() => {
    setUsedLicenses(takenLicenses(seatTakers));
    setUsedSkillDive(takenSkillDiveSeats(seatTakers));
  }, [allInvites]);

  // A count, ∞, or "Learn more" where the plan offers Skill Dive but the account hasn't bought
  // it. A Skill Dive zero isn't highlighted the way the training zero is.
  const skillDiveHeaderAvailability = () => {
    if (skillDiveAvailability.mode === 'not_on_plan') {
      return (
        canGetSkillDive && (
          <GetSkillDiveLink
            onClick={() => window.open(SKILL_DIVE_LEARN_MORE_URL, '_blank', 'noopener,noreferrer')}
          >
            {SKILL_DIVE_LEARN_MORE.HEADER}
          </GetSkillDiveLink>
        )
      );
    }
    return (
      <b>
        {seatsAvailable(
          skillDiveAvailability.mode === 'unlimited' ? '∞' : Math.max(skillDiveRemaining, 0)
        )}
      </b>
    );
  };

  // Today's step shows the licenses banner once there are more invitees than licenses. With the
  // Skill Dive flow it follows the live count, as the Skill Dive banner does.
  const showLicensesBanner = skillDiveFlow
    ? typeof licensesRemaining === 'number' && licensesRemaining <= 0
    : !(typeof account.licenses_available === 'boolean' && account.licenses_available === true) &&
      (typeof account.licenses_available === 'number'
        ? account.licenses_available < allInvites.length
        : false);

  // The Skill Dive desktop layout: the header and every row share one column template.
  const skillDiveLayout = skillDiveFlow && !isMobile;
  const columns = skillDiveColumns(hasTeam);

  return (
    <>
      <TitleContainer>
        <BodyTitle>{skillDiveFlow ? 'Assign access' : 'Adjust users settings'}</BodyTitle>
        {/* With the Skill Dive flow on, availability moves into the column headers. */}
        {isDesktop && !skillDiveFlow && (
          <div>
            <span>Licenses available: </span>
            <b style={{ color: typeof licensesRemaining === 'number' && '#F28815' }}>
              {licensesRemaining}
            </b>
          </div>
        )}
      </TitleContainer>
      {showLicensesBanner &&
        !isEditing &&
        (skillDiveFlow ? (
          <LicenseBanner
            title={NO_TRAINING_LICENSES_BANNER.TITLE}
            body={NO_TRAINING_LICENSES_BANNER.BODY}
            buttonLabel={NO_TRAINING_LICENSES_BANNER.BUTTON}
            href={`mailto:${INE_CONTACT_SUPPORT}`}
          />
        ) : (
          <Advise
            title="You have assigned all of your available licenses."
            body="You can still invite users without a license. However, they will not have access to training content."
            buttonLabel="Get more licenses"
          />
        ))}
      {/* Not drawn in the design; it follows the training banner's. */}
      {skillDiveFlow &&
        skillDiveAvailability.mode === 'count' &&
        skillDiveRemaining <= 0 &&
        skillDiveGained > 0 &&
        !isEditing && (
          <LicenseBanner
            title={NO_SKILL_DIVE_LICENSES_BANNER.TITLE}
            body={NO_SKILL_DIVE_LICENSES_BANNER.BODY}
            buttonLabel={NO_SKILL_DIVE_LICENSES_BANNER.BUTTON}
            href={SKILL_DIVE_CONTACT_HREF}
          />
        )}
      {!isEditing && (
        <Container $skillDiveLayout={skillDiveLayout} $teamGaps={hasTeam}>
          {skillDiveLayout && (
            <HeaderRowContainer $columns={columns} $teamGaps={hasTeam}>
              <span />
              <span />
              {hasTeam && (
                <HeaderTeamManagerRow>
                  <span>Team</span>
                  <span>Manager</span>
                </HeaderTeamManagerRow>
              )}
              <HeaderAvailabilityCell
                $alert={typeof licensesRemaining === 'number' && licensesRemaining <= 0}
              >
                <span>Training</span>
                <b>
                  {seatsAvailable(
                    typeof licensesRemaining === 'number'
                      ? Math.max(licensesRemaining, 0)
                      : licensesRemaining
                  )}
                </b>
              </HeaderAvailabilityCell>
              <HeaderAvailabilityCell>
                {skillDiveAvailability.mode === 'not_on_plan' ? (
                  <HeaderLabelWithInfo>
                    Skill Dive
                    <Tooltip
                      placement="top"
                      trigger={['hover', 'focus']}
                      overlayInnerStyle={NOT_ON_PLAN_TOOLTIP_STYLE}
                      title={SKILL_DIVE_NOT_ON_PLAN.LABEL}
                    >
                      <span role="img" aria-label={SKILL_DIVE_NOT_ON_PLAN.LABEL} tabIndex={0}>
                        <FeatherIcon
                          icon="info"
                          size={16}
                          color={colors.palette.Business_DataVizLilacDark}
                        />
                      </span>
                    </Tooltip>
                  </HeaderLabelWithInfo>
                ) : (
                  <span>Skill Dive</span>
                )}
                {skillDiveHeaderAvailability()}
              </HeaderAvailabilityCell>
              <span />
            </HeaderRowContainer>
          )}
          {!isMobile && !skillDiveFlow && (
            <HeaderRowContainer>
              <span></span>
              <span>{hasTeam && 'TEAM'}</span>
              <span>{inviteAccOwner ? 'OWNER' : hasTeam && 'MANAGER'}</span>
              <span>LICENSE</span>
            </HeaderRowContainer>
          )}
          <Space
            direction={'vertical'}
            size={skillDiveLayout ? 10 : undefined}
            style={{ width: '100%' }}
          >
            {allInvites.map((invite) => {
              const { email, has_license, is_account_owner, userTeams } = invite;
              const existing = editsExistingUsers ? invite.existing : undefined;
              // Existing users' rows get the users table's license locks.
              const licenseLock = existingLicenseLock(
                { email, is_account_owner, existing: invite.existing },
                viewer
              );
              const noLicenseLeft =
                !has_license && !(licensesLeft > 0) && !(existing?.has_license && !licensesGained);
              const licenseToggle = (
                <Tooltip
                  placement={skillDiveFlow ? 'top' : 'bottom'}
                  // The no-seats message comes before the role lock, as in the users table.
                  overlayInnerStyle={
                    (licenseLock && !noLicenseLeft && SKILL_DIVE_TOOLTIP_STYLE) ||
                    (noLicenseLeft && skillDiveFlow && NO_TRAINING_TOOLTIP_STYLE) ||
                    undefined
                  }
                  title={
                    noLicenseLeft ? (
                      <p
                        className={
                          skillDiveFlow ? 'm-0 text-center' : 'm-0 text-center text-nowrap'
                        }
                      >
                        {skillDiveFlow ? (
                          NO_TRAINING_LICENSES_TOOLTIP
                        ) : (
                          <>
                            You have assigned all of your available licenses. <br />
                            Contact INE to get more.
                          </>
                        )}
                      </p>
                    ) : (
                      licenseLock
                    )
                  }
                  zIndex={9999}
                >
                  <div>
                    <Switch
                      defaultChecked={has_license}
                      disabled={noLicenseLeft || Boolean(licenseLock)}
                      onChange={(checked) => {
                        handleSwitch({ checked, inviteEmail: email, type: 'has_license' });
                      }}
                    />
                  </div>
                </Tooltip>
              );
              const deleteButton = (
                <DeleteButton onClick={() => handleRemoveInvite(email)}>
                  <Icon icon="CircledClose" size="small" />
                </DeleteButton>
              );
              if (skillDiveLayout) {
                return (
                  <RowContainer key={email} $columns={columns} $teamGaps={hasTeam}>
                    <div>
                      <AvatarLetter
                        avatar={invite.avatar}
                        name={invite.name || email}
                        size="invite"
                        avatarSize="invite"
                        tinted
                      />
                    </div>
                    {invite.name ? (
                      <NameEmailCell>
                        <b title={invite.name}>{invite.name}</b>
                        <span title={email}>{email}</span>
                      </NameEmailCell>
                    ) : (
                      <SkillDiveEmail title={email}>{email}</SkillDiveEmail>
                    )}
                    {hasTeam && (
                      <TeamsCell>
                        {userTeams.map((userTeam, index) => {
                          const teamId = userTeam?.team?.id || userTeam?.team_id;
                          const teamName = userTeam?.team?.name || userTeam?.name;
                          return (
                            <TeamManagerRow key={index} $divider={index < userTeams.length - 1}>
                              <span title={teamName}>{teamName}</span>
                              <EndCell>
                                <Switch
                                  variant="checkbox"
                                  aria-label={managerOfLabel(teamName)}
                                  disabled={is_account_owner}
                                  checked={Boolean(userTeam.is_manager)}
                                  onChange={(checked: boolean) =>
                                    handleManagerSwitch({ checked, inviteEmail: email, teamId })
                                  }
                                />
                              </EndCell>
                            </TeamManagerRow>
                          );
                        })}
                      </TeamsCell>
                    )}
                    <CenteredCell>{licenseToggle}</CenteredCell>
                    <CenteredCell>
                      {/* Active users already in the account are changed from the users table,
                          unless their changes are applied from here. */}
                      {invite.id && !editsExistingUsers ? (
                        <SkillDivePlaceholder />
                      ) : (
                        <SkillDiveToggleCell
                          account={account}
                          invite={invite}
                          skillDiveRemaining={skillDiveRemaining}
                          skillDiveGained={skillDiveGained}
                          viewer={viewer}
                          onToggle={handleSkillDiveSwitch}
                        />
                      )}
                    </CenteredCell>
                    <CenteredCell>{deleteButton}</CenteredCell>
                  </RowContainer>
                );
              }
              const licenseSwitch = <SwitchContainer>{licenseToggle}</SwitchContainer>;
              return (
                <RowContainer key={email} onClick={() => handleRowClick(email)}>
                  <div style={{ marginRight: '15px' }}>
                    <AvatarLetter name={email} />
                  </div>
                  {skillDiveFlow ? (
                    <SkillDiveEmail title={email}>{email}</SkillDiveEmail>
                  ) : (
                    <LabelEmail>{email}</LabelEmail>
                  )}
                  <div>
                    {hasTeam &&
                      userTeams.map((userTeam, index) => {
                        const teamId = userTeam?.team?.id || userTeam?.team_id;
                        const teamName = userTeam?.team?.name || userTeam?.name;
                        return (
                          <UserTeamContainer
                            key={index}
                            borderBottom={index < userTeams.length - 1}
                          >
                            {/* On mobile the manager choice is made on the invitee's edit
                                screen, so the list only names the teams. */}
                            {skillDiveFlow ? (
                              <div>{teamName}</div>
                            ) : (
                              <>
                                <div>{teamName}</div>
                                <Switch
                                  disabled={is_account_owner}
                                  defaultChecked={userTeam.is_manager}
                                  onChange={(checked) =>
                                    handleManagerSwitch({ checked, inviteEmail: email, teamId })
                                  }
                                />
                              </>
                            )}
                          </UserTeamContainer>
                        );
                      })}
                    {inviteAccOwner && (
                      <SwitchContainer style={{ justifyContent: 'flex-end', marginRight: '5%' }}>
                        <Switch defaultChecked={is_account_owner} disabled />
                      </SwitchContainer>
                    )}
                  </div>
                  <div className={'actions-container'}>
                    {isDesktop && (
                      <>
                        {licenseSwitch}
                        {deleteButton}
                      </>
                    )}
                    {isMobile && (
                      <FeatherIcon
                        icon={'chevron-right'}
                        color={colors.palette.Business_DataVizLilacLighter}
                      />
                    )}
                  </div>
                </RowContainer>
              );
            })}
          </Space>
        </Container>
      )}
      {isEditing && (
        <EditInvite
          footer={true}
          onInviteUpdate={handleUpdateEdit}
          onCancelInvite={() => onEdit(false)}
          onRemoveInvite={handleRemoveInvite}
          email={inviteEdit.email}
          userTeams={inviteEdit.userTeams}
          hasLicense={inviteEdit.has_license}
          hasSkillDive={inviteEdit.has_skill_dive}
          isManager={inviteEdit.is_manager}
          licensesLeft={licensesLeft}
          licensesGained={licensesGained}
          licenseLock={existingLicenseLock(inviteEdit, viewer)}
          skillDiveLock={existingRoleLock(inviteEdit, viewer)}
          isAccountOwner={Boolean(inviteEdit.is_account_owner)}
          account={account}
          skillDiveRemaining={skillDiveRemaining}
          skillDiveGained={skillDiveGained}
          skillDiveFlow={skillDiveFlow && (!inviteEdit.id || editsExistingUsers)}
          existing={editsExistingUsers ? inviteEdit.existing : undefined}
          canChangeTeams={!(skillDiveFlow && teamImplied)}
        />
      )}
    </>
  );
};

export default AdjustUsersSettings;
